/**
 * Get an array from a given value.
 * @param {any} value - The value to convert to an array.
 * @returns {any[]} - The array representation of the value.
 */
export function getArray(value) {
    return Array.isArray(value) ? value : [];
}

/**
 * Get an object from a given value.
 * @param {any} value - The value to convert to an object.
 * @returns {object} - The object representation of the value.
 */
export function getObject(value) {
    return typeof value === 'object' && value !== null ? value : {};
}

/**
 * Get the length of an array.
 * @param {any[]} value - The array to get the length of.
 * @returns {number} - The length of the array.
 */
export function getArrayLength(value) {
    return getArray(value).length;
}

/**
 * Get the length of an object.
 * @param {any} value - The object to get the length of.
 * @returns {number} - The length of the object.
 */
export function getObjectLength(value) {
    return Object.keys(getObject(value)).length;
}

/**
 * Check if an array is truthy.
 * @param {any[]} value - The array to check.
 * @returns {boolean} - True if the array is truthy, false otherwise.
 */
export function isArrayTruthy(value) {
    return getArray(value).length > 0;
}

/**
 * Check if an object is truthy.
 * @param {any} value - The object to check.
 * @returns {boolean} - True if the object is truthy, false otherwise.
 */
export function isObjectTruthy(value) {
    return Object.keys(getObject(value)).length  > 0;
}

/**
 * Check if a string is valid based on the provided options.
 * @param {string} str - The string to check.
 * @param {object} options - Additional options for validation.
 * @returns {boolean} - True if the string is valid, false otherwise.
 */
export const isString = (str, options = {}) => {
    if (typeof str !== "string") return false;
  
    // Additional validations based on options:
    if (options.minLength && str.length < options.minLength) return false;
    if (options.maxLength && str.length > options.maxLength) return false;
    if (options.requiredPattern && !options.requiredPattern.test(str)) return false;
    if (options.disallowedPattern && options.disallowedPattern.test(str)) return false;
  
    return true;
}

/**
 * Check if a value is truthy.
 * @param {any} value - The value to check.
 * @returns {boolean} - True if the value is truthy, false otherwise.
 */
export const isTruthy = (value) => {
    const falsyValues = [undefined, null, 0, false, '', NaN, 'false'];
    return !falsyValues.includes(value);
}

/**
 * Check if a value is falsy.
 * @param {any} value - The value to check.
 * @returns {boolean} - True if the value is falsy, false otherwise.
 */
export const isFalsy = (value) => {
    const falsyValues = [undefined, null, 0, false, '', NaN, 'false'];
    return falsyValues.includes(value);
}

/**
 * Check if a value is null, undefined, NaN, or a string or number that represents zero.
 * @param {any} value - The value to check.
 * @returns {boolean} - True if the value is null, undefined, NaN, or a zero-like string or number, false otherwise.
 */
export const isNull = (value) => {
    return value === null || value === undefined || Number.isNaN(value) ||
           (typeof value === 'string' && ['', '0', '0.00', '0.000', 'null', 'undefined'].includes(value.trim())) ||
           (typeof value === 'number' && (value === 0 || value === 0.0 || value === 0.00 || value === 0.000));
}

export const safeReplace = (input, search, replacement) => {
    try {
      return typeof input === 'string' ? input.replaceAll(search, replacement) : '';
    } catch (e) {
      console.error('safeReplace error:', e, 'input:', input);
      return '';
    }
};

export const smoothScrollToTop = () => {
    const scrollElement = document.scrollingElement || document.documentElement;

    if (scrollElement.scrollTo) {
        scrollElement.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
        scrollElement.scrollTop = 0;
    }
};
